/**
 * VoicePairingModal — modal fullscreen com QR code pro pareamento voice
 * (web /ia, Fase Voice S2).
 *
 * Espelha apps/extension/src/sidebar/components/ai/VoicePairingModal.tsx,
 * adaptado para web:
 *   - Recebe `t` por prop em vez de strings hardcoded
 *   - QR code via lib `qrcode` (privacy-friendly)
 *   - Fallback CDN se geração local falhar (URL contém só o token,
 *     sem PII — risco aceitável)
 *
 * Estados visuais (state prop):
 *   waiting      → "Escaneia com o celular pra começar a gravar"
 *   paired       → "📱 Celular pareado — aguardando gravação"
 *   recording    → "● GRAVANDO no celular..."
 *   transcribing → "🔄 Transcrevendo áudio..."
 *   error        → mensagem em coral
 */
import { useEffect, useMemo, useState, type FC } from 'react';
import QRCode from 'qrcode';
import type { VoiceState } from '../hooks/useVoicePairing';

export interface VoicePairingModalProps {
  readonly state: VoiceState;
  /** URL codificada no QR (ex: https://ia.drpera.com.br/mic/<token>). */
  readonly qrUrl: string | null;
  /** Última transcrição recebida (mostrada no rodapé). */
  readonly lastTranscript: string | null;
  /** Unix ms da expiração — alimenta countdown. */
  readonly expiresAt: number | null;
  /** Mensagem de erro (quando state === 'error'). */
  readonly errorMessage?: string | null;
  /** Fecha modal sem cancelar sessão (X superior, click backdrop). */
  readonly onClose: () => void;
  /** Cancela pareamento (fecha SSE + reset). */
  readonly onCancel: () => void;
  /** Função de tradução do useTranslations. */
  readonly t: (key: string, vars?: Record<string, string | number>) => string;
}

/* ------------------------------------------------------------------------- */
/* Helpers puros (testáveis)                                                   */
/* ------------------------------------------------------------------------- */

/** Formata countdown "M:SS" a partir de ms restantes. Pure. */
export function formatCountdown(msRemaining: number): string {
  if (msRemaining <= 0) return '0:00';
  const totalSec = Math.floor(msRemaining / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

/** Trunca transcript pra exibição (limit chars). Pure. */
export function truncateTranscript(text: string, limit: number = 80): string {
  if (text.length <= limit) return text;
  return `${text.slice(0, limit)}...`;
}

/** Chave i18n + cor semântica por VoiceState. Pure. */
export function getStatusByState(state: VoiceState): {
  textKey: string;
  color: 'green' | 'coral' | 'amber';
} {
  switch (state) {
    case 'requesting':
      return { textKey: 'voice.pairing.requesting', color: 'amber' };
    case 'waiting':
      return { textKey: 'voice.pairing.waiting', color: 'green' };
    case 'paired':
      return { textKey: 'voice.pairing.paired', color: 'green' };
    case 'recording':
      return { textKey: 'voice.pairing.recording', color: 'coral' };
    case 'transcribing':
      return { textKey: 'voice.pairing.transcribing', color: 'amber' };
    case 'error':
      return { textKey: 'voice.pairing.error_connection', color: 'coral' };
    case 'tier_blocked':
      return { textKey: 'voice.pairing.tier_blocked', color: 'coral' };
    case 'idle':
      return { textKey: 'voice.pairing.expired', color: 'coral' };
    default:
      return { textKey: 'voice.pairing.waiting', color: 'green' };
  }
}

/* ------------------------------------------------------------------------- */
/* Componente                                                                  */
/* ------------------------------------------------------------------------- */

const VoicePairingModal: FC<VoicePairingModalProps> = ({
  state,
  qrUrl,
  lastTranscript,
  expiresAt,
  errorMessage,
  onClose,
  onCancel,
  t,
}) => {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  // `now` é só pra forçar re-render do countdown. Começa em 0 pra ser puro
  // no body — o tick do useEffect logo abaixo atualiza pro Date.now real.
  const [now, setNow] = useState<number>(0);

  // Gera QR code quando qrUrl muda.
  useEffect(() => {
    let alive = true;
    if (qrUrl === null) {
      setQrDataUrl(null);
      return () => {
        alive = false;
      };
    }
    void QRCode.toDataURL(qrUrl, {
      width: 256,
      errorCorrectionLevel: 'M',
      margin: 2,
      color: { dark: '#7C7CF8', light: '#222532' },
    })
      .then((url) => {
        if (alive) setQrDataUrl(url);
      })
      .catch(() => {
        // Fallback CDN se geração local falhar (URL só carrega token, sem PII)
        if (alive) {
          const enc = encodeURIComponent(qrUrl);
          setQrDataUrl(
            `https://api.qrserver.com/v1/create-qr-code/?data=${enc}&size=256x256&color=7C7CF8&bgcolor=222532`,
          );
        }
      });
    return () => {
      alive = false;
    };
  }, [qrUrl]);

  // Tick a cada 1s pro countdown — primeiro set imediato pra evitar 0:00 inicial
  useEffect(() => {
    if (expiresAt === null) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const countdown = useMemo(() => {
    if (expiresAt === null) return null;
    return formatCountdown(expiresAt - now);
  }, [expiresAt, now]);

  const status = getStatusByState(state);
  const statusText = t(status.textKey);

  // Claude Design: live/active voice states keep semantic color + glow.
  // 'green' = paired/waiting (voice is live) — allowed per design rules.
  // 'coral' = error/idle/tier_blocked — mapped to var(--violet) at rest.
  // 'amber' = requesting/transcribing — kept as-is (neutral live indicator).
  const isLiveGreen = status.color === 'green';
  const isError = status.color === 'coral';
  const statusColor = isLiveGreen
    ? 'var(--green-fluor)'
    : isError
    ? 'var(--ink-light)'
    : '#FFC857';
  const statusShadow = isLiveGreen
    ? '0 0 6px rgba(0,255,65,0.55), 0 0 14px rgba(0,255,65,0.25)'
    : 'none';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="voice-pairing-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1500,
        background: 'rgba(34,37,50,0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          maxHeight: '92vh',
          background: 'var(--ink-soft)',
          border: '1px solid var(--violet)',
          boxShadow: '0 0 24px rgba(124,124,248,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header modal — banner + close */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 12px',
            borderBottom: '1px solid rgba(124,124,248,0.30)',
            background: 'var(--ink)',
            flexShrink: 0,
          }}
        >
          <h2
            id="voice-pairing-title"
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              fontWeight: 700,
              color: '#FFFFFF',
              textShadow: 'none',
              letterSpacing: '0.04em',
              margin: 0,
              whiteSpace: 'pre',
            }}
          >
            {t('voice.pairing.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{
              background: 'transparent',
              border: '1px solid transparent',
              color: 'var(--ink-mute)',
              fontFamily: 'var(--font-sans)',
              fontSize: 18,
              cursor: 'pointer',
              padding: '2px 6px',
              lineHeight: 1,
              transition: 'color 150ms ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--ink-mute)')}
          >
            ✕
          </button>
        </div>

        {/* QR code centralizado */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            gap: 12,
          }}
        >
          {qrDataUrl !== null && qrUrl !== null ? (
            <img
              src={qrDataUrl}
              alt={t('voice.pairing.qr_alt')}
              width={256}
              height={256}
              style={{
                imageRendering: 'pixelated',
                border: '1px solid var(--violet)',
                boxShadow: '0 0 24px rgba(124,124,248,0.18)',
                background: 'var(--ink)',
              }}
            />
          ) : (
            <div
              aria-label={t('voice.pairing.qr_waiting')}
              style={{
                width: 256,
                height: 256,
                border: '1px dashed rgba(124,124,248,0.30)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(124,124,248,0.6)',
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
              }}
            >
              {state === 'requesting'
                ? t('voice.pairing.qr_generating')
                : t('voice.pairing.qr_none')}
            </div>
          )}

          {/* Status text */}
          <div
            role="status"
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              color: statusColor,
              textShadow: statusShadow,
              letterSpacing: '0.02em',
              textAlign: 'center',
              minHeight: 18,
              padding: '0 8px',
              whiteSpace: 'normal',
              maxWidth: '100%',
              lineHeight: 1.4,
            }}
          >
            {statusText}
          </div>

          {/* Erro detalhado */}
          {state === 'error' && errorMessage !== null && errorMessage !== undefined && errorMessage.length > 0 && (
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                color: 'var(--ink-light)',
                textAlign: 'center',
                padding: '6px 10px',
                background: 'rgba(124,124,248,0.06)',
                border: '1px solid rgba(124,124,248,0.30)',
              }}
            >
              {errorMessage}
            </p>
          )}

          {/* Countdown — só mostra durante sessão ativa */}
          {countdown !== null && state !== 'error' && state !== 'idle' && (
            <div
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                color: 'var(--ink-mute)',
                letterSpacing: '0.03em',
              }}
            >
              {t('voice.pairing.expires_in', { time: countdown })}
            </div>
          )}

          {/* Última transcrição — estado ativo de voice, verde permitido */}
          {lastTranscript !== null && lastTranscript.length > 0 && (
            <div
              style={{
                width: '100%',
                padding: 10,
                background: 'rgba(124,124,248,0.06)',
                border: '1px solid rgba(124,124,248,0.30)',
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                // Green kept: transcript displays while voice is live/recording
                color: 'var(--green-fluor)',
                textShadow: '0 0 6px rgba(0,255,65,0.55)',
                lineHeight: 1.4,
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 11,
                  fontWeight: 600,
                  marginBottom: 4,
                  letterSpacing: '0.04em',
                  color: 'var(--ink-mute)',
                  textShadow: 'none',
                }}
              >
                {t('voice.pairing.last_transcript')}
              </div>
              {truncateTranscript(lastTranscript, 80)}
            </div>
          )}
        </div>

        {/* Footer — cancelar pareamento */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '10px 12px',
            borderTop: '1px solid rgba(124,124,248,0.30)',
            background: 'var(--ink)',
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '8px 20px',
              background: 'transparent',
              color: 'var(--ink-light)',
              border: '1px solid rgba(124,124,248,0.40)',
              fontFamily: 'var(--font-sans)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              borderRadius: 0,
              boxShadow: 'none',
              transition: 'border-color 180ms ease, box-shadow 180ms ease, color 180ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                '0 0 14px rgba(255,106,0,0.8), 0 0 32px rgba(255,106,0,0.5)';
              e.currentTarget.style.borderColor = '#FF6A00';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = 'rgba(124,124,248,0.40)';
              e.currentTarget.style.color = 'var(--ink-light)';
            }}
          >
            {t('voice.pairing.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoicePairingModal;
